import { Game } from "./Game";
import { Gameplay, GameResults, TIMER } from "./Gameplay/Gameplay";
import { Ball, eKickOffResult, KickOffResEstimate } from "./Gameplay/Player/Ball";
import { RoundManager } from "./Gameplay/Round/RoundManager";
import { TargetHitEntry } from "./Gameplay/Targets/TargetHitEntry";
import { Base64 } from "./Utils/Base64";



const enum eRepDataBufPos {
    /**
     * Verze dat
     */
    dataVersion = 0,
    /**
     * Celkova velikost dat
     */
    dataSize = 1,
    /**
     * UID hry
     */
    gameUID = 5,
    /**
     * Cas hry ve vterinach
     */
    gameTime = 6,
    /**
     * Pocet pauz, za ktere byla udelena penalizace
     */
    pauseCnt = 8,
    /**
     * Celkove ziskane skore
     */
    score = 9,
    /**
     * Uprava skore za pauzy, ktere snizily skore pod 0
     */
    pauseScoreCorrection = 13,
    /**
     * Duvod ukonceni hry (0 = pres "submit", ostatni podle hry)
     */
    gameEndReason = 15,
    /**
     * Seed hry
     */
    gameSeed = 16,
    /**
     * Celkovy pocet odehranych kol (vcetne pokusu na stejne kolo)
     */
    recordCnt = 20,
    /**
     * Data hry
     */
    roundData = 21,
}



class ReplayDataManager {
    private static readonly DATA_VERSION = 4;
    private static readonly GAME_UID = 105;

    private _bufView: DataView;
    private _bufOffset: number;
    /**
     * Celkovy pocet bytu v bufferu. Dokud kolo hry neni ukonceno, tak se jeho data nepocitaji do celkove delky dat.
     */
    private _bufTotByteLen: number;
    /**
     * Offset na vysledek prave zaznamenavaneho kola hry
     */
    private _roundResBufOffset: number;

    get isOpen(): boolean { return this._isOpen; }
    private _isOpen: boolean;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        // Vytvorit buffer a view do nej
        this._bufView = new DataView(new ArrayBuffer(10 * 1024));

        // Ulozit zakladni data o hre
        this._bufView.setUint8(eRepDataBufPos.dataVersion, ReplayDataManager.DATA_VERSION);
        this._bufView.setUint8(eRepDataBufPos.gameUID, ReplayDataManager.GAME_UID);
        this._bufView.setUint32(eRepDataBufPos.gameSeed, Game.seed);

        this._isOpen = false;

        // Zachytavat zpravy
        let events = Gameplay.instance.events;

        events.on(RepData.EVENT_CLOSE, this.handleClose, this);
        events.on(RepData.EVENT_RESET, this.handleReset, this);
        events.on(RepData.EVENT_PAUSE, this.handlePausePenalty, this);
        events.on(RepData.EVENT_SAVE_HITS, this.handleHitEntries, this);
        events.on(RoundManager.EVENT_ROUND_READY, this.handleRoundStart, this);
        events.on(Ball.EVENT_KICKED_OFF1, this.handleKickOff, this);
        events.on(Ball.EVENT_FLIGHT_END, this.handleRoundEnd, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleReset(): void {
        // Resetovat pocet zaznamu v buffer
        this._bufView.setUint8(eRepDataBufPos.recordCnt, 0);
        // Nastavit offset na data kola
        this._bufOffset = eRepDataBufPos.roundData;
        this._bufTotByteLen = this._bufOffset;

        this._isOpen = true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleClose(results: GameResults): void {
        if (!this._isOpen)
            return;

        this._isOpen = false;

        let gameplay = Gameplay.instance;

        // Cas hry
        this._bufView.setUint16(eRepDataBufPos.gameTime, Math.round(results.playTime / 1000));
        // Celkove skore
        this._bufView.setUint32(eRepDataBufPos.score, gameplay.score);
        // Duvod ukonceni hry
        this._bufView.setUint8(eRepDataBufPos.gameEndReason, results.endReason);
        // Celkova velikost dat
        this._bufView.setUint32(eRepDataBufPos.dataSize, this._bufTotByteLen);

        // Prevest na Base64 string a odeslat
        Gameplay.instance.events.fire(RepData.EVENT_GET_DATA, Base64.encode(this._bufView.buffer, 0, this._bufTotByteLen));
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handlePausePenalty(compensation: number): void {
        if (this._isOpen) {
            this._bufView.setUint8(eRepDataBufPos.pauseCnt, this._bufView.getUint8(eRepDataBufPos.pauseCnt) + 1);
            this._bufView.setUint16(eRepDataBufPos.pauseScoreCorrection, this._bufView.getUint16(eRepDataBufPos.pauseScoreCorrection) + compensation);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleRoundStart(): void {
        if (this._isOpen) {
            let roundMng = RoundManager.instance;

            // Index pokusu
            this._bufView.setUint8(this._bufOffset++, roundMng.roundAttemptId);

            if (roundMng.roundAttemptId == 0) {
                let roundSetup = roundMng.roundSetup;

                // UID nastaveni
                this._bufView.setUint8(this._bufOffset++, roundSetup.setupUID);

                // Zrcadleni sceny
                this._bufView.setUint8(this._bufOffset++, roundMng.roundSetup.flippedHorizontally ? 1 : 0);

                // KickOff pozice
                let kickOffPos = roundMng.roundSetup.kickOffPos;

                this._bufView.setFloat32(this._bufOffset, kickOffPos.x);
                this._bufOffset += 4;
                this._bufView.setFloat32(this._bufOffset, kickOffPos.z);
                this._bufOffset += 4;
            }

            // Cas zahajeni pokusu
            this._bufView.setUint32(this._bufOffset, roundMng.roundStartTime);
            this._bufOffset += 4;

            // Vysledek kola
            this._roundResBufOffset = this._bufOffset;
            this._bufView.setUint8(this._bufOffset++, 0);

            // Pocet zaznamu o zasahu terce
            this._bufView.setUint8(this._bufOffset++, 0);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleKickOff(resEst: KickOffResEstimate, dirAngle: number, upPower: number): void {
        if (this._isOpen) {
            // Cas vykopnuti
            this._bufView.setUint32(this._bufOffset, Math.round(TIMER.time));
            this._bufOffset += 4;

            // Odhadovana pozice mice v brane
            this._bufView.setInt16(this._bufOffset, Math.round(resEst.goalPos.x * 1000));
            this._bufOffset += 2;
            this._bufView.setInt16(this._bufOffset, Math.round(resEst.goalPos.y * 1000));
            this._bufOffset += 2;

            // Odhadovany vysledek
            this._bufView.setUint8(this._bufOffset++, resEst.result);

            // Smer vykopnuti
            this._bufView.setInt16(this._bufOffset, Math.round(dirAngle * pc.math.RAD_TO_DEG * 100));
            this._bufOffset += 2;
            // Sila smerem nahoru
            this._bufView.setUint16(this._bufOffset, Math.round(upPower * 1000));
            this._bufOffset += 2;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleHitEntries(entries: TargetHitEntry[]): void {
        if (this._isOpen) {
            // Celkovy pocet hit entries        
            this._bufView.setUint8(this._roundResBufOffset + 1, entries.length);

            // Jednotlive hit entry
            entries.forEach((entry: TargetHitEntry): void => {
                // Pozice mice
                this._bufView.setFloat32(this._bufOffset, entry.ballPos.x);
                this._bufOffset += 4;
                this._bufView.setFloat32(this._bufOffset, entry.ballPos.y);
                this._bufOffset += 4;
                // Typ terce
                this._bufView.setUint8(this._bufOffset++, entry.target.type.type);
                // Velikost terce
                this._bufView.setFloat32(this._bufOffset, entry.targetRadius);
                this._bufOffset += 4;
                // Pozice terce
                this._bufView.setFloat32(this._bufOffset, entry.targetPos.x);
                this._bufOffset += 4;
                this._bufView.setFloat32(this._bufOffset, entry.targetPos.y);
                this._bufOffset += 4;
            });
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleRoundEnd(res: eKickOffResult): void {
        if (this._isOpen) {
            // Ulozit vysledek kola
            this._bufView.setUint8(this._roundResBufOffset, res);
            // Navysit pocet zaznamu
            this._bufView.setUint8(eRepDataBufPos.recordCnt, this._bufView.getUint8(eRepDataBufPos.recordCnt) + 1);

            // Upravit celkovou velikost dat v bufferu
            this._bufTotByteLen = this._bufOffset;
        }
    }
}



export class RepData {
    /**
     * Reset replay dat
     */
    static readonly EVENT_RESET = "rd:reset";
    /**
     * Zavreni replay dat (pak je jiz nebude mozne menit)
     * Parametry:
     * results: GameResults
     */
    static readonly EVENT_CLOSE = "rd:close";
    /**
     * Ulozeni aktualnich zasahu do tercu
     * Parametry:
     * - hitEntries: TargetHitEntry[]
     */
    static readonly EVENT_SAVE_HITS = "rd:saveHits";
    /**
     * Penalizace za pauzu
     * Parametry:
     * - compensation: number Hodnota kompenzace skore
     */
    static readonly EVENT_PAUSE = "rd:pause";
    /**
     * Udalost vyvolana managerem, pres kterou predava base64 replay data
     */
    static readonly EVENT_GET_DATA = "rd:getData";

    static get instance(): RepData { return this._instance; }
    private static _instance: RepData;

    /**
     * (RO) Vysledna replay data
     */
    get data(): string { return this._data; }
    private _data: string;
    /**
     * (RO) True, pokud jsou replay data otevrena a je mozne do nich zaznamenavat
     */
    get isOpen(): boolean { return this._rdMng.isOpen; }

    private _rdMng: ReplayDataManager;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        RepData._instance = this;
        this._rdMng = new ReplayDataManager();
        Gameplay.instance.events.on(RepData.EVENT_GET_DATA, (data: string): void => { this._data = data; });
    }
}